import { useState } from 'react';
import { AlertTriangle, ExternalLink, FileSignature, FileUp, Plus } from 'lucide-react';
import { Button, EmptyState, ErrorState, Skeleton, StatusBadge } from '@/design/components';
import { errorMessage } from '@/lib/errors';
import { formatDateTime } from '@/lib/dates';
import { useMe } from '@/auth/useMe';
import { Link } from 'react-router-dom';
import {
  useContractTemplates,
  useCreateFromTemplate,
  useRenderedContract,
} from './templatesApi';
import {
  contractStatusLabel,
  contractStatusTone,
  uploadHrDoc,
  useContractMutations,
  useContracts,
  useHrDocUrl,
  type Contract,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * חוזי העבודה של עובד אחד, בצד הניהול (מיגרציה 092).
 *
 * כרגע את הנוסח מקלידים או מעלים כקובץ. ספריית התבניות לסקטורים
 * השונים, עם מילוי אוטומטי של פרטי העובד, היא הצעד הבא — והיא
 * תייצר בדיוק את אותה רשומה.
 */
export function ContractsPanel({
  employeeId,
  jobTitleId,
}: {
  employeeId: string;
  /** לפיו מוצעות קודם התבניות שמיועדות לתפקיד הזה. */
  jobTitleId: string | null;
}) {
  const { me } = useMe();
  const contracts = useContracts(employeeId);
  const { create, cancel } = useContractMutations(employeeId);

  const [isAdding, setIsAdding] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [filePath, setFilePath] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setIsAdding(false);
    setTitle('');
    setBody('');
    setFilePath(null);
  };

  return (
    <div className="grid gap-3 rounded-md border border-line bg-raised p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-label font-medium">חוזי עבודה</span>
        {!isAdding && templateId === null && (
          <Button variant="secondary" onClick={() => setIsAdding(true)}>
            <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
            חוזה ידני
          </Button>
        )}
      </div>

      {/* הדרך הרגילה: תבנית שמתמלאת לבד. "חוזה ידני" נשאר למקרה
          החריג, ולכן הוא השני ולא הראשון. */}
      {!isAdding && (
        <FromTemplate
          employeeId={employeeId}
          jobTitleId={jobTitleId}
          templateId={templateId}
          onPick={setTemplateId}
          onError={setError}
        />
      )}

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {isAdding && (
        <form
          className="grid gap-3 rounded-sm border border-line bg-cream p-3"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            create
              .mutateAsync({
                title: title.trim(),
                body: body.trim() || null,
                file_path: filePath,
                created_by: me?.employeeId ?? null,
              })
              .then(reset)
              .catch((caught: unknown) => setError(errorMessage(caught)));
          }}
        >
          <div className="grid gap-1">
            <label htmlFor="contract-title" className="text-label font-medium">
              שם החוזה *
            </label>
            <input
              id="contract-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="חוזה העסקה"
              className={FIELD}
            />
          </div>

          <div className="grid gap-1">
            <label htmlFor="contract-body" className="text-label font-medium">
              נוסח החוזה
            </label>
            <textarea
              id="contract-body"
              rows={8}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className={`${FIELD} py-2`}
            />
            <p className="m-0 text-body-sm text-muted">
              זה הנוסח שהעובד/ת יראו ויחתמו עליו. אחרי החתימה הוא נעול לשינוי.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-sm border border-muted px-3 text-body">
              <FileUp size={18} strokeWidth={1.75} aria-hidden="true" />
              {filePath ? 'החלפת הקובץ' : 'צירוף קובץ PDF'}
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  setError(null);
                  setBusy(true);
                  uploadHrDoc(employeeId, 'contract', file)
                    .then(setFilePath)
                    .catch((caught: unknown) => setError(errorMessage(caught)))
                    .finally(() => setBusy(false));
                }}
                className="sr-only"
              />
            </label>
            {busy && <span className="text-body-sm">מעלה…</span>}
            {filePath && !busy && <span className="text-body-sm text-good">קובץ צורף.</span>}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              variant="primary"
              disabled={busy || create.isPending || title.trim() === ''}
            >
              שליחה לחתימה
            </Button>
            <Button variant="secondary" onClick={reset}>
              ביטול
            </Button>
          </div>
          <p className="m-0 text-body-sm text-muted">
            השליחה מוציאה לעובד/ת התראה, והחוזה מחכה באזור האישי שלהם.
          </p>
        </form>
      )}

      {contracts.isPending && <Skeleton className="h-16" />}
      {contracts.isError && <ErrorState onRetry={() => void contracts.refetch()} />}
      {contracts.data?.length === 0 && !isAdding && (
        <EmptyState title="אין חוזים" description="אפשר להוציא חוזה ראשון לחתימה." />
      )}

      <ul className="grid list-none gap-2 p-0">
        {(contracts.data ?? []).map((contract) => (
          <ContractRow
            key={contract.id}
            contract={contract}
            onCancel={() =>
              void cancel
                .mutateAsync(contract.id)
                .catch((caught: unknown) => setError(errorMessage(caught)))
            }
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * בחירת תבנית, תצוגה מקדימה מלאה, והוצאה.
 *
 * התצוגה המקדימה היא הנוסח האמיתי עם הפרטים של העובד/ת, ולא
 * דוגמה: אם חסר משהו בכרטיס רואים את זה כאן ולא אחרי השליחה.
 */
function FromTemplate({
  employeeId,
  jobTitleId,
  templateId,
  onPick,
  onError,
}: {
  employeeId: string;
  jobTitleId: string | null;
  templateId: string | null;
  onPick: (id: string | null) => void;
  onError: (message: string | null) => void;
}) {
  const templates = useContractTemplates(jobTitleId);
  const rendered = useRenderedContract(templateId, employeeId);
  const create = useCreateFromTemplate(employeeId);

  const list = templates.data ?? [];

  if (templates.isPending) return <Skeleton className="h-10" />;

  if (list.length === 0) {
    return (
      <p className="m-0 text-body-sm text-muted">
        אין עדיין תבניות חוזים.{' '}
        <Link to="/admin/employees/contract-templates" className="text-ink">
          אפשר לבנות תבנית
        </Link>{' '}
        ואז להוציא ממנה חוזה בלחיצה.
      </p>
    );
  }

  const missing = rendered.data?.missing ?? [];

  return (
    <div className="grid gap-2">
      <div className="grid gap-1">
        <label htmlFor="pick-template" className="text-label font-medium">
          הוצאת חוזה מתבנית
        </label>
        <select
          id="pick-template"
          value={templateId ?? ''}
          onChange={(event) => {
            onError(null);
            onPick(event.target.value || null);
          }}
          className={FIELD}
        >
          <option value="">בחירת תבנית</option>
          {list.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name}
              {template.suits ? ' · מתאימה לתפקיד' : ''}
            </option>
          ))}
        </select>
      </div>

      {templateId && rendered.isPending && <Skeleton className="h-32" />}

      {templateId && rendered.data && (
        <>
          <p className="m-0 text-body-sm text-muted">כך זה ייראה לעובד/ת:</p>
          <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-sm border border-line bg-surface p-3 text-body text-ink">
            {rendered.data.body}
          </div>

          {missing.length > 0 && (
            <p className="m-0 flex items-start gap-2 text-body-sm text-warn">
              <AlertTriangle
                size={16}
                strokeWidth={1.75}
                aria-hidden="true"
                className="mt-0.5 shrink-0"
              />
              <span>
                חסר בכרטיס: {missing.join(', ')}. אפשר להשלים למעלה, ואז להוציא את החוזה.
              </span>
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              disabled={create.isPending || missing.length > 0}
              onClick={() => {
                onError(null);
                create
                  .mutateAsync(templateId)
                  .then(() => onPick(null))
                  .catch((caught: unknown) => onError(errorMessage(caught)));
              }}
            >
              שליחה לחתימה
            </Button>
            <Button variant="secondary" onClick={() => onPick(null)}>
              ביטול
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ContractRow({ contract, onCancel }: { contract: Contract; onCancel: () => void }) {
  const file = useHrDocUrl(contract.file_path);
  const signature = useHrDocUrl(contract.signature_path);

  return (
    <li className="grid gap-2 rounded-sm border border-line bg-surface p-3">
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature size={18} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        <span className="text-body font-medium">{contract.title}</span>
        <StatusBadge tone={contractStatusTone[contract.status]}>
          {contractStatusLabel[contract.status]}
        </StatusBadge>
        <span className="grow" />
        {contract.status === 'pending' && (
          <Button variant="ghost" onClick={onCancel}>
            ביטול החוזה
          </Button>
        )}
      </div>

      {contract.signed_at && (
        <p className="m-0 text-body-sm text-muted">
          נחתם על ידי {contract.signed_name} ב{formatDateTime(contract.signed_at)}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {file.data && (
          <a
            href={file.data}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-body-sm text-ink"
          >
            <ExternalLink size={14} strokeWidth={1.75} aria-hidden="true" />
            הקובץ המצורף
          </a>
        )}
        {signature.data && (
          <img
            src={signature.data}
            alt={`החתימה של ${contract.signed_name ?? 'העובד'}`}
            className="h-16 rounded-sm border border-line bg-surface"
          />
        )}
      </div>
    </li>
  );
}
